import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { apiErr } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { MessageSquarePlus, Trash2, ShieldCheck, AlertTriangle, Loader2, Save, Stethoscope, PenTool, LayoutTemplate, MapPin, BookText, FileSignature } from "lucide-react";
import { toast } from "sonner";
import SignatureForm from "@/components/SignatureForm";
import ClinicTemplatesEditor from "@/components/ClinicTemplatesEditor";
import ProtocolTemplatesEditor from "@/components/ProtocolTemplatesEditor";
import VisitDefaultsEditor from "@/components/VisitDefaultsEditor";
import IopFormulaSettings from "@/components/IopFormulaSettings";

const AREAS = [
  { v: "cartella", label: "Cartella clinica" },
  { v: "agenda", label: "Agenda" },
  { v: "terapia", label: "Terapia" },
  { v: "referti", label: "Referti" },
  { v: "ia", label: "IA" },
  { v: "farmaci", label: "Farmaci" },
  { v: "impostazioni", label: "Impostazioni" },
  { v: "altro", label: "Altro" },
];
const PRIORITIES = [
  { v: "bassa", label: "Bassa" },
  { v: "media", label: "Media" },
  { v: "alta", label: "Alta" },
];
const STATUS_LABEL = {
  nuovo: { label: "Nuovo", cls: "bg-slate-100 text-slate-700" },
  letto: { label: "Letto", cls: "bg-blue-100 text-blue-700" },
  in_valutazione: { label: "In valutazione", cls: "bg-amber-100 text-amber-700" },
  risolto: { label: "Risolto", cls: "bg-emerald-100 text-emerald-700" },
};

export default function Settings() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [area, setArea] = useState("altro");
  const [priority, setPriority] = useState("");
  const [saving, setSaving] = useState(false);
  // Sedi (per riepilogo, gestione completa su /sedi)
  const [sites, setSites] = useState([]);
  // Medico defaults + firma
  const [defaults, setDefaults] = useState(null);
  const [signature, setSignature] = useState(null);
  const [defaultsLoading, setDefaultsLoading] = useState(true);
  const [defaultsSaving, setDefaultsSaving] = useState(false);
  const [tab, setTab] = useState("visit_defaults"); // visit_defaults | templates | signature | sedi | feedback

  const loadSettings = async () => {
    setDefaultsLoading(true);
    try {
      const { data } = await api.get("/medico/settings");
      setDefaults(data.defaults || {});
      setSignature(data.signature || {});
    } catch (e) {
      toast.error(apiErr(e, "Errore caricamento impostazioni"));
    } finally {
      setDefaultsLoading(false);
    }
  };

  const saveSettings = async () => {
    setDefaultsSaving(true);
    try {
      await api.put("/medico/settings", { defaults, signature });
      toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error(apiErr(e, "Errore salvataggio"));
    } finally {
      setDefaultsSaving(false);
    }
  };

  const loadSites = async () => {
    try { const { data } = await api.get("/medico/sites"); setSites(data || []); }
    catch { setSites([]); }
  };

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/medico/feedback"); setItems(data || []); }
    catch (e) { toast.error(apiErr(e, "Errore caricamento commenti")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); loadSites(); loadSettings(); }, []);

  const submit = async (e) => {
    e?.preventDefault();
    if (!title.trim() || !body.trim()) { toast.error("Titolo e testo richiesti"); return; }
    setSaving(true);
    try {
      await api.post("/medico/feedback", { title: title.trim(), body: body.trim(), area, priority: priority || null });
      setTitle(""); setBody(""); setArea("altro"); setPriority("");
      toast.success("Commento inviato");
      load();
    } catch (e) { toast.error(apiErr(e, "Errore invio")); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare il commento?")) return;
    try { await api.delete(`/medico/feedback/${id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(apiErr(e, "Errore")); }
  };

  return (
    <AppShell title="Impostazioni" subtitle="Referti base, modelli cartella, firma, feedback">
      {/* Tabs */}
      <div className="mb-4 inline-flex p-1 bg-white border border-[#E2E8F0] rounded-xl flex-wrap" data-testid="settings-tabs">
        {[
          { v: "visit_defaults", label: "Referti base", icon: FileSignature },
          { v: "templates", label: "Modelli cartella", icon: LayoutTemplate },
          { v: "protocols", label: "Protocolli per diagnosi", icon: BookText },
          { v: "signature", label: "Firma & Ordine", icon: PenTool },
          { v: "sedi", label: "Sedi / Studi", icon: MapPin },
          { v: "feedback", label: "Commenti software", icon: MessageSquarePlus },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.v}
              data-testid={`settings-tab-${t.v}`}
              onClick={() => setTab(t.v)}
              className={`px-3 py-2 rounded-lg font-bold text-xs flex items-center gap-1.5 transition ${tab === t.v ? "bg-brand-navy text-white" : "text-slate-600 hover:bg-slate-100"}`}
            >
              <Icon size={13} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* "Default cartella" è stato unificato in "Referti base" (fonte unica per i default clinici).
          I vecchi valori (medico_settings.defaults) restano nel DB come fallback: nessun dato perso. */}
      {tab === "defaults" && (
        <div className="space-y-4" data-testid="tab-defaults">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-900">
            <b>Default cartella è stato unificato in Referti base.</b> I default clinici (tono normale, segmento anteriore,
            fondo oculare, ecc.) si impostano ora in un solo posto: <b>Referti base</b>, per ciascun modello cartella.
          </div>
          <IopFormulaSettings />
          <VisitDefaultsEditor />
        </div>
      )}

      {/* TAB: TEMPLATES MODELLI CARTELLA */}
      {tab === "templates" && (
        <div data-testid="tab-templates">
          <ClinicTemplatesEditor />
        </div>
      )}

      {/* TAB: REFERTI BASE / TEMPLATE VISITA SANA PER TIPOLOGIA */}
      {tab === "visit_defaults" && (
        <div data-testid="tab-visit-defaults" className="space-y-4">
          <IopFormulaSettings />
          <VisitDefaultsEditor />
        </div>
      )}

      {/* TAB: PROTOCOLLI PER DIAGNOSI / HASHTAG */}
      {tab === "protocols" && (
        <div data-testid="tab-protocols">
          <ProtocolTemplatesEditor />
        </div>
      )}

      {/* TAB: FIRMA E ORDINE */}
      {tab === "signature" && (
        <div className="space-y-4" data-testid="tab-signature">
          <div className="bg-brand-azure/5 border border-brand-azure/30 rounded-xl p-4 text-xs text-brand-navy">
            Questi dati compaiono in fondo a <b>ogni referto/PDF/email</b> generato dal tuo account. Sono privati e specifici per te.
          </div>
          {defaultsLoading || !signature ? (
            <div className="bg-white rounded-xl border border-[#E2E8F0] p-8 text-center text-slate-500">
              <Loader2 className="animate-spin mx-auto mb-2" /> Caricamento firma…
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-[#E2E8F0] p-5">
              <SignatureForm signature={signature} setSignature={setSignature} />
              <div className="mt-4 flex justify-end">
                <button
                  data-testid="save-signature"
                  onClick={saveSettings}
                  disabled={defaultsSaving}
                  className="bg-brand-navy hover:bg-brand-navyDark text-white font-bold px-5 py-2 rounded-lg flex items-center gap-2 disabled:opacity-60"
                >
                  {defaultsSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  Salva firma
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB: SEDI / STUDI (riepilogo + link a /sedi per gestione completa) */}
      {tab === "sedi" && (
        <div className="space-y-4" data-testid="tab-sedi">
          <div className="bg-brand-azure/5 border border-brand-azure/30 rounded-xl p-4 text-xs text-brand-navy">
            Le sedi compaiono nel referto PDF (data/ora + nome sede + indirizzo) e nei filtri storico. La gestione completa (creazione, colore, attivazione) è su <b>Sedi / Studi</b>.
          </div>
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <MapPin className="text-brand-azure" size={18} />
                <h3 className="font-extrabold text-brand-navy">Le tue sedi</h3>
                <span className="text-xs text-slate-500">{sites.length} / 10</span>
              </div>
              <button data-testid="open-sedi-page" onClick={() => nav("/sedi")} className="bg-brand-navy hover:bg-brand-navyDark text-white font-bold text-xs px-3 py-2 rounded-lg">Gestisci sedi</button>
            </div>
            {sites.length === 0 ? (
              <div className="text-center text-slate-500 italic text-sm py-6">Nessuna sede configurata. Premi <b>Gestisci sedi</b> per aggiungerne.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="settings-sites-list">
                {sites.map((s) => (
                  <div key={s.id} className="border border-[#E2E8F0] rounded-lg p-3 flex items-center gap-3" data-testid={`settings-site-${s.id}`}>
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: s.color || "#2C4667" }}></span>
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-brand-navy truncate">{s.name} {!s.active && <span className="text-[10px] text-slate-400 ml-1">(disattiva)</span>}</div>
                      <div className="text-xs text-slate-500 truncate">{[s.address, s.city, s.province].filter(Boolean).join(" · ") || "—"}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="text-[10px] text-slate-500 italic flex items-start gap-1.5 px-1">
            <ShieldCheck size={11} className="mt-0.5" />
            Le configurazioni tecniche del sistema (Brevo / API / Sicurezza / Backup) sono gestite esclusivamente da Admin PrimeVision per garantire separazione tra ruolo operativo e ruolo di sistema.
          </div>
        </div>
      )}

      {/* TAB: FEEDBACK */}
      {tab === "feedback" && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="tab-feedback">
        {/* Composer */}
        <div className="lg:col-span-1 bg-white rounded-xl border border-[#E2E8F0] p-5" data-testid="settings-feedback-composer">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquarePlus className="text-brand-azure" size={18} />
            <h3 className="font-extrabold text-brand-navy">Commenti sul software</h3>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed mb-3">
            Scrivi suggerimenti, idee o segnalazioni sul software PrimeVision. Sono feedback di prodotto, visibili da Admin Primevision.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-[11px] text-amber-900 leading-relaxed flex gap-1.5 items-start mb-3">
            <AlertTriangle size={12} className="mt-0.5 text-amber-700 shrink-0" />
            <span><b>Privacy:</b> non inserire qui dati di pazienti, cartelle o referti clinici. Solo feedback sul software.</span>
          </div>

          <form onSubmit={submit} className="space-y-2">
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-500">Titolo breve</label>
              <input data-testid="feedback-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="es. Vorrei un campo per…"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0] focus:outline-none focus:border-brand-azure text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-500">Commento</label>
              <textarea data-testid="feedback-body" rows={4} value={body} onChange={(e) => setBody(e.target.value)} maxLength={5000}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0] focus:outline-none focus:border-brand-azure text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500">Area</label>
                <select data-testid="feedback-area" value={area} onChange={(e) => setArea(e.target.value)} className="mt-1 w-full px-2.5 py-2 rounded-lg border border-[#E2E8F0] text-sm bg-white">
                  {AREAS.map((a) => <option key={a.v} value={a.v}>{a.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500">Priorità</label>
                <select data-testid="feedback-priority" value={priority} onChange={(e) => setPriority(e.target.value)} className="mt-1 w-full px-2.5 py-2 rounded-lg border border-[#E2E8F0] text-sm bg-white">
                  <option value="">—</option>
                  {PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
                </select>
              </div>
            </div>
            <button data-testid="feedback-submit" disabled={saving} type="submit"
              className="w-full bg-brand-navy hover:bg-brand-navyDark text-white font-bold py-2 rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 size={14} className="animate-spin" />} Invia commento
            </button>
          </form>
        </div>

        {/* Lista */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-[#E2E8F0] p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-extrabold text-brand-navy">I miei commenti</h3>
            <span className="text-xs text-slate-500">{items.length}</span>
          </div>
          {loading && <div className="text-sm text-slate-500">Caricamento…</div>}
          {!loading && items.length === 0 && (
            <div className="text-sm text-slate-500 italic py-8 text-center">Nessun commento inviato.</div>
          )}
          <ul className="space-y-2" data-testid="feedback-list">
            {items.map((f) => {
              const st = STATUS_LABEL[f.status] || STATUS_LABEL.nuovo;
              return (
                <li key={f.id} className="border border-[#E2E8F0] rounded-lg p-3" data-testid={`feedback-item-${f.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-brand-navy">{f.title}</div>
                      <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{f.body}</div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-2 flex flex-wrap gap-2 items-center">
                        <span>{new Date(f.created_at).toLocaleString("it-IT")}</span>
                        <span className="text-slate-300">·</span>
                        <span>Area: <b className="text-slate-700">{f.area}</b></span>
                        {f.priority && <><span className="text-slate-300">·</span><span>Priorità: <b className="text-slate-700">{f.priority}</b></span></>}
                        <span className={`ml-auto px-2 py-0.5 rounded font-bold ${st.cls}`}>{st.label}</span>
                      </div>
                    </div>
                    {f.status !== "risolto" && (
                      <button onClick={() => remove(f.id)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`feedback-delete-${f.id}`}><Trash2 size={14} /></button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      )}

      <div className="mt-6 text-[10px] text-slate-400 flex items-start gap-1.5">
        <ShieldCheck size={11} className="mt-0.5" />
        <span>I tuoi commenti sono visibili dal tuo account medico e dall'Admin Primevision come feedback di prodotto. Non contengono dati clinici.</span>
      </div>
    </AppShell>
  );
}
